import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { BLOG_ARTICLES } from './blogArticles';
import css from './BlogPage.module.css';

const LANDING_IMG = '/static/images/landing';

const layoutAreas = `
  topbar
  main
  footer
`;

const HERO_SUBTITLE =
  'Jump into the world of skydiving gear with the Avento Blog, your resource hub for everything about skydiving gear! Whether you are a newly licenced skydiver looking to buy your first rig, or an experienced pro wanting to stay up to date with trends and new items, we got you covered. Have a look for engaging news and carefully researched content on specific topics.';

// The four resource articles keep their own labels and existing URLs.
const RESOURCES = [
  {
    label: 'Harness Sizing',
    title: 'Harness Sizing Guide',
    description:
      'Looking to buy a new rig online but not sure how to make sure the harness actually fits? We prepared a comprehensive guide with calculators.',
    image: `${LANDING_IMG}/harness-sizing.webp`,
    link: '/harness-sizing',
  },
  {
    label: 'Wingloading',
    title: 'Wingloading Calculator',
    description:
      "Feel the urge to get a new wing but not sure what's out there? Our smart WL calculator shows you canopies recommended for your loading.",
    image: `${LANDING_IMG}/wingloading.webp`,
    link: '/wingloading-calculator',
  },
  {
    label: 'Container Sizing',
    title: 'Container Sizing Guide',
    description:
      'When buying a new Rig, ensuring that it will hold your canopies is crucial. This guide bundles resources and manufacturer information on container sizes.',
    image: `${LANDING_IMG}/container-sizing.webp`,
    link: '/container-sizing',
  },
  {
    label: 'Price Calculator',
    title: 'Gear Value Calculator',
    description:
      'Determining a fair price for used skydiving gear is hard. Our pricing Algorithm (Beta) calculates fair price based on multiple factors to give you a reference.',
    image: `${LANDING_IMG}/gear-value.webp`,
    link: '/gear-value-calculator',
  },
];

// Blog articles first (no label), then the resource articles (with labels).
const ARTICLE_CARDS = BLOG_ARTICLES.map(article => ({
  key: article.slug,
  image: article.cardImage,
  label: null,
  title: article.cardTitle,
  description: article.cardDescription,
  link: `/blog/${article.slug}`,
}));

const CARDS = [...ARTICLE_CARDS, ...RESOURCES.map(r => ({ ...r, key: r.link }))];

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'Blog',
    name: 'Avento Blog',
    url: `${rootURL}/blog`,
    description:
      'Guides, news, reviews and tutorials about skydiving gear from Avento, the marketplace for used skydiving equipment.',
  };
}

// ---------- Card ----------

const ContentCard = ({ card }) => (
  <article className={css.card}>
    <a href={card.link} className={css.cardImageLink}>
      <div className={css.cardImageWrap}>
        <img
          src={card.image}
          alt={card.title}
          className={css.cardImage}
          width={600}
          height={400}
          loading="lazy"
        />
        {card.label ? <span className={css.cardLabel}>{card.label}</span> : null}
      </div>
    </a>
    <div className={css.cardContent}>
      <h2 className={css.cardTitle}>{card.title}</h2>
      <p className={css.cardDesc}>{card.description}</p>
      <a href={card.link} className={css.cardReadBtn}>
        Read &rarr;
      </a>
    </div>
  </article>
);

// ---------- Main component ----------

const BlogPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();
  const rootURL = config.marketplaceRootURL || '';

  return (
    <Page
      title="Avento Blog | Guides, news, reviews and tutorials for skydiving gear"
      description="Your resource hub for everything about skydiving gear. Engaging news and carefully researched guides, reviews and tutorials from the Avento marketplace."
      schema={buildPageSchema(rootURL)}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="BlogPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                <section className={css.hero}>
                  <div className={css.container}>
                    <h1 className={css.heroTitle}>Avento blog</h1>
                    <p className={css.heroSubtitle}>{HERO_SUBTITLE}</p>
                  </div>
                </section>

                <section className={css.articlesSection}>
                  <div className={css.container}>
                    <div className={css.cardsGrid}>
                      {CARDS.map(card => (
                        <ContentCard key={card.key} card={card} />
                      ))}
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const BlogPage = connect(mapStateToProps)(BlogPageComponent);

export default BlogPage;
